'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Props for `FeatureOverview`.
 */
export type FeatureOverviewProps =
  SliceComponentProps<Content.FeatureOverviewSlice>;

/**
 * Component for "FeatureOverview" Slices.
 */
const FeatureOverview: FC<FeatureOverviewProps> = ({ slice }) => {
  const { section_heading, section_description, features, side_image } =
    slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headerRef, delay: '0ms' },
      { ref: featuresRef, delay: '150ms' },
      { ref: imageRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#0E1010] py-12 md:py-20 px-4 md:px-8 lg:px-[150px]"
    >
      <div className="max-w-[1140px] mx-auto flex flex-col gap-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className="flex flex-col gap-6 items-center text-center"
        >
          {isFilled.richText(section_heading) && (
            <div className="text-[28px] md:text-[40px] font-semibold leading-[1.2] text-white">
              <PrismicRichText
                field={section_heading}
                components={{
                  heading2: ({ children }) => <h2>{children}</h2>,
                }}
              />
            </div>
          )}

          {isFilled.richText(section_description) && (
            <div className="text-base md:text-lg font-normal leading-[1.5] text-white max-w-[746px]">
              <PrismicRichText field={section_description} />
            </div>
          )}
        </div>

        {/* Content: Features + Image */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-[60px] items-center">
          {/* Left Column - Features List */}
          <div
            ref={featuresRef}
            className="flex flex-col gap-2 w-full lg:w-[520px]"
          >
            {features?.map((feature, index) => (
              <div
                key={index}
                className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.4)] to-transparent p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)]"
              >
                <div className="flex flex-col gap-2">
                  {isFilled.keyText(feature.feature_title) && (
                    <h3 className="text-xl md:text-2xl font-bold leading-[1.4] text-[#05F273]">
                      {feature.feature_title}
                    </h3>
                  )}
                  {isFilled.keyText(feature.feature_description) && (
                    <p className="text-base md:text-lg font-normal leading-[1.4] text-white">
                      {feature.feature_description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Right Column - Side Image */}
          {isFilled.image(side_image) && (
            <div
              ref={imageRef}
              className="flex-1 flex items-center justify-center"
            >
              <div className="rounded-2xl overflow-hidden">
                <PrismicNextImage
                  field={side_image}
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureOverview;
